import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import i18n from '@/i18n'
import { logger } from '@/lib/logger'

interface ErrorBoundaryProps {
  children: ReactNode
  title?: string
  description?: string
  resetKeys?: unknown[]
}

interface ErrorBoundaryState {
  hasError: boolean
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true }
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.state.hasError && resetKeysChanged(prevProps.resetKeys, this.props.resetKeys)) {
      this.setState({ hasError: false })
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error('ErrorBoundary caught render error', error, errorInfo)
    logger.reportClientError(error, { componentStack: errorInfo.componentStack ?? '' })
  }

  render() {
    if (!this.state.hasError) return this.props.children

    return (
      <Card className="m-4 rounded-lg border-destructive/30 bg-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <AlertTriangle className="size-4 text-destructive" />
            {this.props.title ?? i18n.t('errors.boundary.title')}
          </CardTitle>
          <CardDescription>
            {this.props.description ?? i18n.t('errors.boundary.description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button type="button" variant="outline" onClick={() => window.location.reload()}>
            <RefreshCw className="size-4" />
            {i18n.t('errors.boundary.refresh')}
          </Button>
        </CardContent>
      </Card>
    )
  }
}

function resetKeysChanged(prevKeys?: unknown[], nextKeys?: unknown[]) {
  if (!prevKeys && !nextKeys) return false
  if (!prevKeys || !nextKeys) return true
  if (prevKeys.length !== nextKeys.length) return true
  return prevKeys.some((key, index) => !Object.is(key, nextKeys[index]))
}
